// apps/web/src/features/crm/leads/activity/components/LeadCommunicationDrawer.tsx
//
// Capture drawer for a typed CALL/MESSAGE communication (#1809, #1807).
// One component serves both kinds — the `kind` prop drives the title and the
// activity row that the backend emits. Mirrors `LeadTaskEditorDrawer`'s form
// pattern so the unsaved-changes guard is auto-wired (`useForm` co-located
// with the `EditorDrawer` mount so the live dirty flag reaches the guard).
//
// `occurredAt` on the wire is a full ISO 8601 string. The native
// `<input type="datetime-local">` speaks local `YYYY-MM-DDTHH:mm`, so we
// re-anchor through `new Date(value).toISOString()` on submit.
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  CreateLeadCommunicationRequestSchema,
  LeadCommunicationKind,
  LeadCommunicationDirection,
  LeadCommunicationChannel,
  type CreateLeadCommunicationRequest,
} from '@magimp/shared';
import { useCreateLeadCommunication } from '@magimp/shared-hooks';
import {
  Button,
  EditorDrawer,
  FormRow,
  Input,
  Select,
  Textarea,
  useEditorGuard,
  type SelectOption,
} from '../../../../../ui/index.js';
import { useToastStore } from '../../../../../stores/toast.js';
import { errorMessage } from '../../../../../api/errors.js';

export interface LeadCommunicationDrawerProps {
  leadId: string;
  kind: LeadCommunicationKind;
  onClose: () => void;
}

/** Local `datetime-local` value (or empty) → ISO 8601 timestamp, or null when cleared. */
export function dateTimeInputToIso(value: string): string | null {
  if (value === '') return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/** ISO 8601 → `YYYY-MM-DDTHH:mm` for the native datetime-local input.
 * Uses LOCAL date getters so the round-trip is stable in the user's timezone. */
export function isoToDateTimeInput(iso: string): string {
  if (iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function LeadCommunicationDrawer({ leadId, kind, onClose }: LeadCommunicationDrawerProps) {
  const { t } = useTranslation(['leads', 'common']);
  const show = useToastStore((s) => s.show);
  const create = useCreateLeadCommunication(leadId);
  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<CreateLeadCommunicationRequest>({
    resolver: zodResolver(CreateLeadCommunicationRequestSchema),
    defaultValues: {
      kind,
      direction: LeadCommunicationDirection.OUTBOUND,
      channel: LeadCommunicationChannel.PHONE,
      occurredAt: '',
      text: '',
    },
  });

  const directionOptions: SelectOption[] = [
    {
      value: LeadCommunicationDirection.OUTBOUND,
      label: t('leads:activity.communications.direction.OUTBOUND'),
    },
    {
      value: LeadCommunicationDirection.INBOUND,
      label: t('leads:activity.communications.direction.INBOUND'),
    },
  ];
  const channelOptions: SelectOption[] = [
    {
      value: LeadCommunicationChannel.PHONE,
      label: t('leads:activity.communications.channel.PHONE'),
    },
    {
      value: LeadCommunicationChannel.WHATSAPP,
      label: t('leads:activity.communications.channel.WHATSAPP'),
    },
    {
      value: LeadCommunicationChannel.TELEGRAM,
      label: t('leads:activity.communications.channel.TELEGRAM'),
    },
  ];

  const title =
    kind === LeadCommunicationKind.CALL
      ? t('leads:activity.communications.callTitle')
      : t('leads:activity.communications.messageTitle');

  const onSubmit = (values: CreateLeadCommunicationRequest) => {
    create.mutate(values, {
      onSuccess: () => {
        show({ tone: 'success', text: t('leads:activity.communications.toasts.created') });
        onClose();
      },
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <EditorDrawer dirty={isDirty} busy={create.isPending} onConfirmClose={onClose} width={460}>
      <CommunicationFormBody
        title={title}
        isDirty={isDirty}
        busy={create.isPending}
        onSubmit={handleSubmit(onSubmit)}
        onClose={onClose}
      >
        <FormRow
          label={t('leads:activity.communications.directionLabel')}
          error={errors.direction?.message}
        >
          <Controller
            control={control}
            name="direction"
            render={({ field }) => (
              <Select value={field.value} onChange={field.onChange} options={directionOptions} />
            )}
          />
        </FormRow>
        <FormRow
          label={t('leads:activity.communications.channelLabel')}
          error={errors.channel?.message}
        >
          <Controller
            control={control}
            name="channel"
            render={({ field }) => (
              <Select value={field.value} onChange={field.onChange} options={channelOptions} />
            )}
          />
        </FormRow>
        <FormRow
          label={t('leads:activity.communications.occurredAtLabel')}
          error={errors.occurredAt?.message}
        >
          <Controller
            control={control}
            name="occurredAt"
            render={({ field }) => (
              <Input
                type="datetime-local"
                value={isoToDateTimeInput(field.value)}
                onChange={(v) => field.onChange(dateTimeInputToIso(v) ?? '')}
                invalid={!!errors.occurredAt}
              />
            )}
          />
        </FormRow>
        <FormRow label={t('leads:activity.communications.textLabel')} error={errors.text?.message}>
          <Controller
            control={control}
            name="text"
            render={({ field }) => (
              <Textarea
                value={field.value}
                onChange={field.onChange}
                rows={4}
                placeholder={t('leads:activity.communications.textPlaceholder')}
                ariaLabel={t('leads:activity.communications.textLabel')}
              />
            )}
          />
        </FormRow>
      </CommunicationFormBody>
    </EditorDrawer>
  );
}

function CommunicationFormBody({
  title,
  isDirty,
  busy,
  onSubmit,
  onClose,
  children,
}: {
  title: string;
  isDirty: boolean;
  busy: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation(['leads', 'common']);
  // WHY: Cancel routes through confirmDestructive so a dirty form prompts before discarding.
  const { confirmDestructive } = useEditorGuard();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      noValidate
      data-testid="lead-communication-editor"
      data-dirty={isDirty ? 'true' : 'false'}
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{title}</h2>
      {children}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <Button kind="ghost" onClick={() => confirmDestructive(onClose)} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" kind="primary" disabled={busy} data-testid="lead-communication-save">
          {t('leads:activity.communications.submit')}
        </Button>
      </div>
    </form>
  );
}
